import * as React from "react";
import classNames from "classnames";
import { withRouter } from "react-router";
import { DragDropContext } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import TouchBackend from "react-dnd-touch-backend";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import { ConnectedComposerView } from "./composer-view";
import { ConnectedReservationList } from "./reservation-list";
import { ConnectedReservationInfobox } from "./reservation-infobox";
import { ConnectedReservationSlider } from "./reservation-slider";
import { ConnectedComposerDragLayer } from "./composer-drag-layer";
import { ConnectedReservationInfobar } from "./reservation-infobar";
import { ConnectedTableLinkInfobar } from "./table-link-infobar";
import { ConnectedTiteledPaper } from "../layout/titeled-paper";
import { InjectedRouterProps } from "../../router/props";
import { Routes } from "../../router/routes";

export interface DayOverviewProps {}

export interface DropTargetData {
	tableId: string;
}

interface ConnectedDayOverviewProps extends WithStyles<typeof styles> {
	layout: "desktop" | "tablet" | "phone";
}

type Props = DayOverviewProps &
	ConnectedDayOverviewProps &
	InjectedRouterProps &
	InjectedTranslateProps;

export class DayOverview extends React.Component<Props, {}> {
	public componentDidMount() {
		if (this.props.layout === "phone") {
			this.props.history.push(Routes.BookingPlanList);
		}
	}

	renderDesktop = () => {
		const { classes } = this.props;

		return (
			<React.Fragment>
				<div className={classes.columnLeft}>
					<ConnectedTiteledPaper title="Belegungsplan">
						<ConnectedComposerView />
					</ConnectedTiteledPaper>
				</div>
				<div className={classes.columnRight}>
					<ConnectedReservationList />
					<ConnectedReservationInfobox />
				</div>
			</React.Fragment>
		);
	};

	renderTablet = () => {
		const { classes } = this.props;

		return (
			<div className={classes.fullWidth}>
				<ConnectedComposerView />

				<div className={classes.bottomContent}>
					<ConnectedTableLinkInfobar />
					<ConnectedReservationInfobar />
					<ConnectedReservationSlider />
				</div>
			</div>
		);
	};

	renderPhone = () => {
		return <div />;
	};

	renderMainContent = () => {
		const { layout } = this.props;

		switch (layout) {
			case "desktop":
				return this.renderDesktop();

			case "tablet":
				return this.renderTablet();

			default:
				return this.renderPhone();
		}
	};

	render() {
		const { classes, layout } = this.props;
		const isMobile = layout !== "desktop";

		return (
			<div className={classes.root}>
				<div
					className={classNames(classes.mainContent, {
						[classes.mainContentFullWidth]: isMobile
					})}
				>
					{this.renderMainContent()}
				</div>
				<ConnectedComposerDragLayer />
			</div>
		);
	}
}

@DragDropContext(
	TouchBackend({
		enableMouseEvents: true
	})
)
class DecoratedDayOverview extends DayOverview {}

const mapStateToProps = (state: StoreState) => ({
	layout: state.layout
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			padding: 25,
			display: "flex",
			flexDirection: "column",
			flexGrow: 1,
			"-webkit-user-select": "none",
			userSelect: "none",
			[theme.breakpoints.down(1600)]: {
				padding: 0
			}
		},
		mainContent: {
			display: "flex",
			flexGrow: 1
		},
		mainContentFullWidth: {
			rlexDirection: "column",
			flexWrap: "wrap"
		},
		columnLeft: {
			display: "flex",
			flexDirection: "column",
			flexGrow: 1,
			marginRight: 25,
			maxWidth: 1200
		},
		columnRight: {
			flexGrow: 0,
			minWidth: 700
		},
		fullWidth: {
			display: "flex",
			flexDirection: "column",
			flexGrow: 1,
			width: "100%",
			marginRight: 0
		},
		bottomContent: {
			height: 100,
			display: "flex",
			flexDirection: "column",
			position: "relative",
			zIndex: 1
		}
	});

export const ConnectedDayOverview = translate("translations")(
	withRouter(
		withStyles(styles)(connect(mapStateToProps)(DecoratedDayOverview))
	)
);
